import React, {
  ChangeEvent,
  forwardRef,
  Fragment,
  HTMLAttributes,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOMServer from 'react-dom/server';
import { HTMLDivElement } from 'window-or-global';
import Icon, { IconName } from '../../icons/icon/icon-component';
import { Modal } from '../../modal/modal-component';
import isMobileDevice from '../../responsive/responsive.util';
import CustomScrollbar, { CustomScrollbarRef } from '../../scrollbar/scrollbar-component';
import Text, { TextRef } from '../text-component/text-component';
import styles from './dropdown-component.module.scss';

export interface DropdownRef {
  select: (key: string | number, toggle?: boolean) => void;
  forceSelect: (toggle?: boolean) => void;
}

//TODO: aux text
type DropdownProps = {
  isDisabled?: boolean;
  /** If true, need to define list.searchableString */
  isSearchable?: boolean;
  isOpen?: boolean;
  placeholder?: string;
  background?: 'white' | 'grey';
  showSeparatorBetweenElements?: boolean;
  defaultSelection?: string | number;
  /** Array of {key: string | number, element, searchableString, groupName} objects */
  list: {
    key: string | number;
    elt: JSX.Element;
    /** used as a display string when an element is selected (only when dropdown is NOT searchable) **/
    displayString?: string;
    searchableString?: string;
    /** one element can belong to multiple groups **/
    group?: string[];
  }[];
  label?: string;
  helpText?: string;
  additionalInfo?: string;
  isValid?: boolean;
  /** ClassName for the input field */
  containerClassName?: string;
  /** Mobile header for searchable dropdown. Should include required elements excluding searchable dropdown.*/
  customSearchHeader?: ReactNode;
  /** ClassName for customizable mobile header. Allows customization of the searchable dropdown by referring to the class customSearchHeaderClassName + '-input'. */
  customSearchHeaderClassName?: string;
  errorMessage?: string;
  showTextOrDivForSearchable?: 'text' | 'div';
  mobileHeaderPlaceholder?: string;
  showSearchIconInOpenSearchableModal?: boolean;
  isSearchableMobileModalFullScreen?: boolean;
  searchableInputType?: string;
  searchableStepValue?: number;
  minNumericValue?: number;
  maxNumericValue?: number;
  selectOnClickOutside?: boolean;
  selectOnEnter?: boolean;
  extraIcon?: IconName;
  prioritiseMobileModalCloseIconClick?: boolean;
  clearSearchTextOnFocus?: boolean;
  /** (key: string | number) => void */
  selectCallback?: (key: string | number, isEltClick?: boolean) => void;
  validationCallback?: (value: string) => boolean | Promise<boolean>;
  onChangeState?: (isOpen: boolean) => void;
  onSearchValueChange?: (value: string) => void;
  searchMobileFullScreenModalCloseCallback?: (key: string | number) => void;
  /* only for tablet + */
  onUserInvalidSelectAttempt?: () => void;
} & HTMLAttributes<HTMLDivElement>;

const DEFAULT_PROPS: Required<Pick<DropdownProps, 'background' | 'isSearchableMobileModalFullScreen' | 'selectOnEnter'>> = {
  background: 'white',
  isSearchableMobileModalFullScreen: true,
  selectOnEnter: true,
};

/**
 * Dropdown component
 *
 * https://www.figma.com/file/jiN4FkdkhDZIWxIkPS6Ru7/design-system?node-id=88%3A121
 */
export const Dropdown = forwardRef<DropdownRef, DropdownProps>((props: DropdownProps, ref) => {
  const {
    isDisabled,
    isSearchable,
    isOpen,
    isValid,
    className,
    placeholder,
    background,
    showSeparatorBetweenElements,
    defaultSelection,
    list,
    label,
    helpText,
    additionalInfo,
    customSearchHeader,
    errorMessage,
    showTextOrDivForSearchable,
    showSearchIconInOpenSearchableModal,
    isSearchableMobileModalFullScreen,
    searchableInputType,
    searchableStepValue,
    minNumericValue,
    maxNumericValue,
    containerClassName,
    customSearchHeaderClassName,
    selectOnClickOutside,
    selectOnEnter,
    extraIcon,
    prioritiseMobileModalCloseIconClick,
    clearSearchTextOnFocus,
    mobileHeaderPlaceholder,
    selectCallback,
    validationCallback,
    onChangeState,
    onSearchValueChange,
    searchMobileFullScreenModalCloseCallback,
    onUserInvalidSelectAttempt,
    ...selectAttributes
  } = { ...DEFAULT_PROPS, ...props }; // <-- merge defaults before destructuring;

  const placeholderEltDefault = useMemo(() => <div>{placeholder}</div>, [placeholder]);

  const defaultSelectionListElt = defaultSelection && list?.filter((x) => x.key === defaultSelection)[0];
  const defaultSelectionElt = (
    <p>
      {defaultSelectionListElt &&
        (!isSearchable && defaultSelectionListElt?.displayString
          ? defaultSelectionListElt?.displayString
          : defaultSelectionListElt?.searchableString)}
    </p>
  );

  const [value, setValue] = useState(defaultSelection);
  const [isDropdownOpen, setIsDropdownOpen] = useState(isOpen ?? false);
  const [isModalOpen, setIsModalOpen] = useState(() => (isMobileDevice() ? isOpen ?? false : false));
  const [placeholderElt, setPlaceholderElt] = useState(defaultSelection ? defaultSelectionElt : placeholderEltDefault);
  const [searchTextValue, setSearchTextValue] = useState(!defaultSelection ? '' : defaultSelection.toString()); // what is displayed in text component for searchable dropdown
  const [dropList, setDropList] = useState(list);
  const [lastListProp, setLastListProp] = useState(list);
  const [isValidState, setIsValidState] = useState(isValid ?? true);
  const [lastValidProp, setLastValidProp] = useState(isValid ?? true); //to store latest isValid prop so as to update isValidState when prop changes
  const [isUserSearching, setIsUserSearching] = useState(false); // used for searchable div only
  const [lastDefaultSelectionProp, setLastDefaultSelectionProp] = useState(defaultSelection); //to keep record of latest prop.defaultSelection
  const [lastIsOpenProp, setLastIsOpenProp] = useState(isOpen);

  //set icon for search dropdown text component
  const searchIcon = isMobileDevice() && isDropdownOpen && showSearchIconInOpenSearchableModal ? 'search' : 'chev-down';

  //call when droplist changes
  const getGroups = useCallback(
    (
      list: {
        key: string | number;
        elt: JSX.Element;
        displayString?: string;
        searchableString?: string;
        group?: string[];
      }[]
    ) => {
      const groups: { [name: string]: (string | number)[] } = {};

      list.forEach((element) => {
        element.group?.forEach((group) => {
          if (!groups[group]) {
            groups[group] = [element.key];
          } else {
            groups[group].push(element.key);
          }
        });
      });
      return groups;
    },
    []
  );

  //when dropList changes update groups
  const groups = useMemo(() => getGroups(dropList), [dropList, getGroups]);

  const node = useRef<HTMLDivElement>(null);
  const scrollbarRef = useRef<CustomScrollbarRef>(null);
  const textRef = useRef<TextRef>(null);
  const mobileHeaderRef = useRef<HTMLDivElement>(null);
  const dropdownContentChildrenRef = useRef<HTMLDivElement>(null);

  const checkValidSearchTextValue = useCallback(
    async (currentSearchText: string) => {
      //checking result of validation callback
      if (validationCallback) {
        setIsValidState(await validationCallback(currentSearchText));
      }
    },
    [validationCallback]
  );

  const filterDropList = useCallback(
    (searchString: string) => {
      if (isSearchable) {
        setDropList(list?.filter((x) => x.searchableString && x.searchableString.toLowerCase().includes(searchString.toLowerCase())));
      }
    },
    [isSearchable, list]
  );

  // If force is not undefined, "toggles" state, opening it if it's closed and closing it if it's open.
  // If force is true, forces state to open. If force is false, forces state to close.
  const toggleIsOpen = useCallback(
    (force?: boolean) => {
      if (isSearchable && showTextOrDivForSearchable === 'div') {
        setIsUserSearching(false);
      }

      setIsDropdownOpen((prevState) => {
        if (prevState === false) filterDropList('');

        const newState = force !== undefined ? force : !prevState;
        onChangeState?.(newState);
        return newState;
      });
    },
    [filterDropList, isSearchable, onChangeState, showTextOrDivForSearchable]
  );

  // when value is selected it sets searchTextValue (if searchable) to
  // the corresponding dropdown item's searchable string else
  // (if not searchable) sets placeholder to the item's element
  const updateValueAndDisplay = useCallback(
    (val?: string | number) => {
      if (list.length && val /** && !isDropdownOpen*/) {
        setValue(val);

        const filteredList = list.filter((x) => x.key === val);

        // matchin element found?
        if (filteredList.length) {
          const listElement = filteredList[0];

          if (isSearchable) {
            setSearchTextValue(listElement.searchableString ?? '');
            if (showTextOrDivForSearchable === 'div') setPlaceholderElt(listElement.elt);
          } else {
            setPlaceholderElt(listElement.displayString ? <p>{listElement.displayString}</p> : listElement.elt);
          }
        }

        // prefer exact match but allow wrong values if searchable with validation callback
        else if (isSearchable && validationCallback) {
          setSearchTextValue(val.toString());
          if (showTextOrDivForSearchable === 'div') setPlaceholderElt(<>{val.toString()}</>);
        }
      }
    },
    [isSearchable, list, showTextOrDivForSearchable, validationCallback]
  );

  const select = useCallback(
    (key: string | number, toggle?: boolean, isEltClick?: boolean) => {
      const toggleDropdown = toggle === undefined ? true : toggle;

      updateValueAndDisplay(key);

      if (isSearchable) {
        // update search state, filter & validate
        const searchElem = list.filter((l) => l.key === key)[0];
        const searchText = searchElem.searchableString ?? '';

        setSearchTextValue(searchText);

        if (showTextOrDivForSearchable === 'div') {
          setPlaceholderElt(searchElem.elt);
        }

        checkValidSearchTextValue(searchText);

        if (toggleDropdown) {
          toggleIsOpen();
        }

        //to update hash for mobile device
        if (isSearchableMobileModalFullScreen && isMobileDevice()) {
          window.history.back();
        }
      } else {
        const elt = list.filter((x) => x.key === key)[0];

        setPlaceholderElt(elt.displayString ? <p>{elt.displayString}</p> : elt.elt);
        toggleIsOpen();
      }

      selectCallback?.(key, isEltClick);
    },
    [
      updateValueAndDisplay,
      isSearchable,
      selectCallback,
      list,
      showTextOrDivForSearchable,
      checkValidSearchTextValue,
      isSearchableMobileModalFullScreen,
      toggleIsOpen,
    ]
  );

  //used to select current search string value
  const forceSelect = useCallback(
    (toggle?: boolean) => {
      const selectValueList = list.filter((l) => l.searchableString?.toLowerCase() === searchTextValue.toLowerCase());
      if (selectValueList.length > 0) {
        select(selectValueList[0].key, toggle);
      } else {
        //checking result of validation callback
        checkValidSearchTextValue(searchTextValue);
        onUserInvalidSelectAttempt?.();
      }
    },
    [checkValidSearchTextValue, searchTextValue, select, list, onUserInvalidSelectAttempt]
  );

  // expose internal methods
  useImperativeHandle(ref, () => ({
    select: select,
    forceSelect: forceSelect,
  }));

  useLayoutEffect(() => {
    // call update scroll to re-calculate scroll length
    scrollbarRef.current?.updateScroll();
  }, []);

  //update drodpown validity when prop isValid changes
  if (isValid !== undefined && isValid !== lastValidProp) {
    setIsValidState(isValid);
    setLastValidProp(isValid);
  }

  //when props defaultSelection changes, update value & placeholder
  if (defaultSelection !== lastDefaultSelectionProp) {
    setLastDefaultSelectionProp(defaultSelection);
    updateValueAndDisplay(defaultSelection);

    if (defaultSelection) {
      if (isValid === false) {
        setPlaceholderElt(<p>{defaultSelection.toString()}</p>);
        if (isSearchable) setSearchTextValue(defaultSelection.toString());
      } else {
        const elt = list?.filter((x) => x.key === defaultSelection)[0];
        setPlaceholderElt(<p>{!isSearchable && elt.displayString ? elt.displayString : elt.searchableString}</p>);
      }
    }
  }

  //when props isOpen changes open/close the popup
  if (isOpen !== lastIsOpenProp) {
    setLastIsOpenProp(isOpen);
    setIsDropdownOpen(isOpen ?? false);
    setIsModalOpen(isMobileDevice() ? isOpen ?? false : false);
  }

  //when props list changes sets value to the latter
  if (
    list.length !== lastListProp.length ||
    list.filter(
      (val, i) =>
        val.key !== lastListProp[i].key || ReactDOMServer.renderToString(val.elt) !== ReactDOMServer.renderToString(lastListProp[i].elt)
    ).length > 0
  ) {
    setDropList(list);
    setLastListProp(list);
  }

  //when dropList changes only show invalid selection if validation callback provided
  if (searchTextValue.length > 0 && isSearchable && !isDropdownOpen && value === undefined && !validationCallback) {
    setSearchTextValue('');
  }

  //when dropList changes only show invalid selection if validation callback provided
  if (
    placeholderElt !== placeholderEltDefault && // compare by reference
    isSearchable &&
    showTextOrDivForSearchable === 'div' &&
    !isDropdownOpen &&
    value === undefined &&
    !validationCallback
  ) {
    setPlaceholderElt(placeholderEltDefault);
  }

  //if isOpen adds event listeners to close dropdown otherwise removes them
  useEffect(() => {
    function onResize() {
      if (isSearchable) {
        //create modal if on mobile else destroy
        setIsModalOpen(isMobileDevice());
      } else {
        if (isMobileDevice()) {
          //enable no-scroll upon resize to mobile if not enabled
          if (!document.body.classList.contains(styles['no-scroll'])) {
            document.body.classList.toggle(styles['no-scroll']);
          }
        }
        //disable no-scroll upon resize to desktop if not already disabled
        else if (document.body.classList.contains(styles['no-scroll'])) {
          document.body.classList.toggle(styles['no-scroll']);
        }
      }

      // call update scroll to re-calculate scroll length
      scrollbarRef.current?.updateScroll();

      if (isMobileDevice() && !isSearchable) {
        document.body.classList.toggle(styles['no-scroll'], isDropdownOpen);
      }
    }

    function onClickOutside(e: MouseEvent) {
      if (e.target && node.current?.contains(e.target as Node)) {
        // inside click
        return;
      }
      // outside click
      setIsDropdownOpen(false);
      if (onChangeState) onChangeState(false);

      if (selectOnClickOutside) {
        //do not force select if clearSearchTextOnFocus is enabled and there is no search text
        if (!(isSearchable && clearSearchTextOnFocus && value && searchTextValue === '')) {
          forceSelect(false);
        }
      }
    }

    if (isDropdownOpen) {
      window.addEventListener('resize', onResize);
      document.addEventListener('mousedown', onClickOutside);
    } else {
      window.removeEventListener('resize', onResize);
      document.removeEventListener('mousedown', onClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      window.removeEventListener('resize', onResize);
    };
  }, [
    forceSelect,
    isDropdownOpen,
    isSearchable,
    selectOnClickOutside,
    clearSearchTextOnFocus,
    searchTextValue,
    value,
    props,
    onChangeState,
  ]);

  //to calculate mobile header height
  useEffect(() => {
    if (isDropdownOpen) {
      const mobileHeaderHeight = (mobileHeaderRef.current?.clientHeight ?? 0) / 10;
      const dropdownContentChildrenHeight = (dropdownContentChildrenRef.current?.clientHeight ?? 0) / 10;

      //36 is the max height for mobile
      const contentHeight =
        dropdownContentChildrenHeight + mobileHeaderHeight > 36 ? 36 - mobileHeaderHeight : dropdownContentChildrenHeight;
      document.documentElement.style.setProperty('--mobile-content-height', contentHeight + 'rem');
    }
  }, [isDropdownOpen]);

  const numericFlag = searchableInputType === 'number';

  const searchDropdownTextContent = (
    <div
      onKeyDown={onSearchDropdownKeyDown}
      onClick={() => isDisabled !== true && onSearchDropdownClick()}
      className={`${styles['search-dropdown-wrapper']} ${isDropdownOpen ? styles['search-dropdown-open'] : ''} ${
        searchIcon === 'search' ? styles['show-mobile-svg'] : styles['hide-mobile-svg']
      }`}
    >
      <Text
        ref={textRef}
        onBlur={() => {
          onTextboxBlur();
        }}
        data-testid='dropdown-search'
        className={isDropdownOpen ? `${customSearchHeaderClassName}-input` : ''}
        autoFocus={isDropdownOpen}
        onFocus={() => {
          if (isSearchable && clearSearchTextOnFocus && searchTextValue.length > 0) {
            setSearchTextValue('');
          }
        }}
        onChange={onChangeSearchText}
        placeholder={placeholder}
        value={searchTextValue}
        // defaultValue={searchTextValue}
        background={background}
        //show search icon on the left in the mobile searchable modal if showSearchIconInMobileSearchableModal is set to true
        icon={searchIcon}
        secondIcon={extraIcon}
        alignIcon={searchIcon === 'search' ? 'left' : 'right'}
        disabled={isDisabled}
        errorMessage={errorMessage}
        isValid={isValidState}
        type={searchableInputType}
        step={numericFlag ? searchableStepValue ?? undefined : undefined}
        min={numericFlag ? minNumericValue ?? undefined : undefined}
        max={numericFlag ? maxNumericValue ?? undefined : undefined}
        showEllipsesOnOverflow={true}
        {...selectAttributes}
      />
    </div>
  );

  const searchDropdown =
    showTextOrDivForSearchable === 'text' ? (
      searchDropdownTextContent
    ) : !isUserSearching && !isDropdownOpen ? (
      <>
        <div
          {...selectAttributes}
          tabIndex={0}
          data-testid='dropdown'
          className={`${styles['option-container']}
           ${isValidState ? '' : styles.invalid}
            ${isDisabled ? styles.disabled : ''} ${containerClassName ?? ''} ${styles['search-div']}`}
          onClick={() => onSearchDropdownClick()}
          onKeyDown={(e) => toggleIsOpenViaEnterKey(e)}
        >
          <div className={styles['selected-option']} data-testid='selected-elt'>
            {placeholderElt}
          </div>
          {extraIcon && <Icon className={styles.icon} name={extraIcon} width={24} height={24}></Icon>}
          <Icon className={styles.icon} name='chev-down' width={24} height={24}></Icon>
        </div>
        {!isValidState && errorMessage !== '' && <p className={styles['error-text']}>{errorMessage}</p>}
      </>
    ) : (
      searchDropdownTextContent
    );

  const dropListContents = (
    <div ref={dropdownContentChildrenRef} className={Object.keys(groups).length > 0 ? styles['search-group-list'] : ''}>
      {additionalInfo && <div className={styles['additional-info']}>{additionalInfo}</div>}
      {dropList &&
        dropList.map((item, index) => {
          return getItemDetails(item, index);
        })}
    </div>
  );

  const groupDropListContents = (
    <div ref={dropdownContentChildrenRef}>
      {additionalInfo && <div className={styles['additional-info']}>{additionalInfo}</div>}
      {groups &&
        dropList &&
        Object.keys(groups).map((groupName) => {
          return (
            <Fragment key={groupName}>
              <div className={styles['group-name']}>{groupName}</div>
              {groups[groupName].map((itemKey, index) => {
                const item = dropList.find((item) => item.key === itemKey);

                //just in case groups has not yet been updated
                return item ? getItemDetails(item, index, groupName) : null;
              })}
            </Fragment>
          );
        })}
    </div>
  );

  const dropdownMobileStyle = {
    '--display-search-modal': isSearchable && isDropdownOpen ? 'block' : 'none',
    '--display-slide-up': isSearchable && isDropdownOpen ? 'none' : 'block',
  } as React.CSSProperties;

  return (
    <div
      ref={node}
      style={dropdownMobileStyle}
      className={`${styles['dropdown-component']} ${className ? className : ''}  ${background ? styles[background] : styles.white}`}
      aria-disabled={isDisabled}
    >
      <div className={styles.info}>
        {label && <label className={styles.label}>{label}</label>}
        {helpText && <p className={styles['help-text']}>{helpText}</p>}
      </div>
      <div className={styles['dropdown-wrapper']}>
        {isSearchable ? (
          //if searchable show modal for mobile device
          <div>
            {isModalOpen && isDropdownOpen && (
              <div className={styles.modal}>
                <Modal
                  headerClassName={customSearchHeaderClassName}
                  headerElt={
                    customSearchHeader ? (
                      <React.Fragment>
                        {customSearchHeader} {searchDropdown}
                      </React.Fragment>
                    ) : (
                      searchDropdown
                    )
                  }
                  isMobileFullScreen={isSearchableMobileModalFullScreen}
                  prioritiseCloseIconClick={prioritiseMobileModalCloseIconClick}
                  children={
                    <div
                      className={`${styles['search-dropdown-content']} ${
                        Object.keys(groups).length > 0 ? styles['search-modal-grouped'] : ''
                      } `}
                    >
                      {Object.keys(groups).length > 0 && searchTextValue === '' ? groupDropListContents : dropListContents}
                    </div>
                  }
                  closeCallback={() => {
                    if (searchMobileFullScreenModalCloseCallback && searchTextValue) {
                      searchMobileFullScreenModalCloseCallback(searchTextValue);
                    }
                    toggleIsOpen(false);
                  }}
                />
              </div>
            )}
            {(!isModalOpen || !isDropdownOpen) && searchDropdown}
          </div>
        ) : (
          <>
            <div
              data-testid='dropdown'
              tabIndex={0}
              {...selectAttributes}
              className={`${styles['option-container']} 
               ${isValidState ? '' : styles.invalid}
              ${isDisabled ? styles.disabled : ''} ${containerClassName ?? ''}`}
              onClick={() => toggleIsOpen()}
              onKeyDown={(e) => toggleIsOpenViaEnterKey(e)}
            >
              <div className={styles['selected-option']} data-testid='selected-elt'>
                {placeholderElt}
              </div>
              {extraIcon && <Icon className={styles.icon} name={extraIcon} width={24} height={24}></Icon>}
              <Icon className={styles.chevron} name='chev-down' width={24} height={24}></Icon>
            </div>
            {!isValidState && errorMessage !== '' && <p className={styles['error-text']}>{errorMessage}</p>}
          </>
        )}
        <div
          className={`${styles['dropdown-content']} ${isDropdownOpen ? '' : 'hide'} ${
            Object.keys(groups).length > 0 ? styles.grouped : ''
          } `}
          onBlur={(e: React.FocusEvent<HTMLDivElement>) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              toggleIsOpen(false);
            }
          }}
        >
          <div ref={mobileHeaderRef} className={styles['mobile-header']}>
            <span className={styles.placeholder}>{mobileHeaderPlaceholder ?? placeholder}</span>
            <Icon name='close' onClick={() => toggleIsOpen()} />
          </div>
          <CustomScrollbar
            className={`${styles['scroll-container']} ${isSearchable ? '' : styles['scroll-container-not-searchable']}`}
            showOnLoad={true}
            ref={scrollbarRef}
          >
            {Object.keys(groups).length > 0 && searchTextValue === '' ? groupDropListContents : dropListContents}
          </CustomScrollbar>
        </div>
      </div>
      <div className={`${styles.overlay} ${!isDropdownOpen ? 'hide' : ''}`} onClick={() => toggleIsOpen()}></div>
    </div>
  );

  function onSearchDropdownClick() {
    setIsUserSearching(true);

    if (isMobileDevice()) {
      //creates modal
      if (!isModalOpen) {
        setIsModalOpen(true);
      }

      //if modal is open do not close upon clicking text component
      if (isDropdownOpen) {
        return;
      }
    } else {
      //destroy modal
      if (isModalOpen) {
        setIsModalOpen(false);
      }
    }
    toggleIsOpen();
  }

  function onSearchDropdownKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (!isDropdownOpen) {
      onSearchDropdownClick();
    } else if (e.key === 'Enter' && selectOnEnter) {
      forceSelect(false);
      textRef.current?.blur();
      toggleIsOpen();
    }
  }

  function toggleIsOpenViaEnterKey(e: React.KeyboardEvent<HTMLDivElement>) {
    //if Enter key pressed
    if (e.key === 'Enter') {
      toggleIsOpen();
    }
  }

  function onChangeSearchText(e: ChangeEvent<HTMLInputElement>) {
    if (value) {
      setValue(undefined);
    }

    setSearchTextValue(e.currentTarget.value);
    if (showTextOrDivForSearchable === 'div') setPlaceholderElt(<>{e.currentTarget.value}</>);

    filterDropList(e.currentTarget.value);

    onSearchValueChange?.(e.currentTarget.value);
  }

  function onTextboxBlur() {
    if (showTextOrDivForSearchable === 'div') setIsUserSearching(false);

    //if user types in text component but does not select value from dropdown list
    if (!value && searchTextValue !== '') {
      //check if contents of text component match element in dropdown list
      list.forEach((element) => {
        if (element.searchableString === searchTextValue) {
          updateValueAndDisplay(element.key);

          return;
        }
      });
    }
  }

  function getItemDetails(
    item: { key: string | number; elt: JSX.Element; searchableString?: string | undefined; group?: string[] | undefined },
    index: number,
    group?: string
  ): JSX.Element {
    return (
      <div
        onClick={() => select(item.key, true, true)}
        onKeyUp={(e) => {
          //select on enter
          if (e.keyCode === 13) select(item.key);
        }}
        className={`${styles['dropdown-item']} ${index === (dropList && dropList.length - 1) ? styles.last : ''} ${
          showSeparatorBetweenElements ? styles['dropdown-item-separator'] : ''
        }`}
        key={item.key}
        id={(selectAttributes.id ? `${selectAttributes.id}_` : '') + item.key.toString() + (group ? `_${group}` : '')}
        data-testid={item.key.toString()}
        tabIndex={0}
      >
        {item.elt}
      </div>
    );
  }
});
